/**
 * Canonical Sport Registry
 * ------------------------------------------------------------------
 * SINGLE SOURCE OF TRUTH for sport *identity* and *common* metadata
 * that is shared across every sport (canonical id, aliases used for
 * normalization, display label, whether the sport allows ties, and the
 * scoring terminology used for standings / records).
 *
 * Sport-SPECIFIC data (the actual stat catalogue, points vs. goals vs.
 * runs, per-sport categories, leaders, line scores, positions) continues
 * to live in the per-sport configs (`sportConfiguration.ts`,
 * `config/sportSlots.ts`). Those modules are expected to *consume* this
 * registry for identity/terminology rather than re-deriving it, so that
 * editing a common point (a label, the tie rule, the scoring noun) in one
 * place updates every sport at once.
 *
 * Anything that previously did
 *   `sport.toLowerCase().replace(/[\s-]/g, '_')`
 * and hand-rolled an alias/fallback table should call `normalizeSport`.
 */

export type CanonicalSport =
  | 'soccer'
  | 'basketball'
  | 'baseball'
  | 'softball'
  | 'flag_football';

/**
 * Scoring terminology for a sport. This is the "common point" abstraction:
 * every sport scores *something* (points / goals / runs) and standings,
 * records and "for / against" columns are rendered from these labels.
 */
export interface ScoringTerminology {
  singular: string;
  plural: string;
  abbreviation: string;
  forLabel: string;
  againstLabel: string;
  perGameLabel: string;
  /** Short column header for the "for" total (e.g. PF / GF / RF). */
  forAbbreviation: string;
  /** Short column header for the "against" total (e.g. PA / GA / RA). */
  againstAbbreviation: string;
}

/**
 * Default standings metric for NEW leagues of a sport. A points-table sport
 * (soccer's 3-1-0) ranks and displays league points; a pct sport ranks by win
 * percentage (ties counted as half-wins). Organizers can still change the
 * point system per league in Settings; this only seeds the creation default.
 */
export interface StandingsDefault {
  usePointSystem: boolean;
  pointsForWin?: number;
  pointsForTie?: number;
  pointsForLoss?: number;
}

export interface SportMeta {
  id: CanonicalSport;
  /** Human-friendly display name. */
  label: string;
  /**
   * Normalized aliases (lowercased, spaces/hyphens -> underscore) that map
   * to this sport. The canonical id is always implicitly included.
   */
  aliases: string[];
  /** Whether games may end in a tie / draw (affects record formatting). */
  allowsTies: boolean;
  /** Shared scoring terminology (the thing that is the same across sports). */
  scoring: ScoringTerminology;
  /** Standings metric seeded into league_settings at creation. */
  standingsDefault: StandingsDefault;
  /**
   * Whether this sport is offered to users when creating a league. Flip this
   * on to launch an additional sport; the creation pickers are driven from it.
   */
  launchEnabled?: boolean;
}

const points: ScoringTerminology = {
  singular: 'point',
  plural: 'points',
  abbreviation: 'PTS',
  forLabel: 'Points For',
  againstLabel: 'Points Against',
  perGameLabel: 'Points/Game',
  forAbbreviation: 'PF',
  againstAbbreviation: 'PA',
};

const goals: ScoringTerminology = {
  singular: 'goal',
  plural: 'goals',
  abbreviation: 'G',
  forLabel: 'Goals For',
  againstLabel: 'Goals Against',
  perGameLabel: 'Goals/Game',
  forAbbreviation: 'GF',
  againstAbbreviation: 'GA',
};

const runs: ScoringTerminology = {
  singular: 'run',
  plural: 'runs',
  abbreviation: 'R',
  forLabel: 'Runs For',
  againstLabel: 'Runs Against',
  perGameLabel: 'Runs/Game',
  forAbbreviation: 'RF',
  againstAbbreviation: 'RA',
};

/** Rank by win percentage (US convention; ties count as half-wins). */
const pctStandings: StandingsDefault = { usePointSystem: false };

export const SPORT_REGISTRY: Record<CanonicalSport, SportMeta> = {
  soccer: {
    id: 'soccer',
    label: 'Soccer',
    aliases: ['soccer'],
    allowsTies: true,
    scoring: goals,
    // Soccer standings are a points table worldwide: 3 for a win, 1 for a
    // draw. Ranking a tie-allowing sport by win pct both shows the wrong
    // metric and can order the table differently (a draw is worth 1/3 of a
    // win in points, not 1/2).
    standingsDefault: { usePointSystem: true, pointsForWin: 3, pointsForTie: 1, pointsForLoss: 0 },
    launchEnabled: true,
  },
  basketball: {
    id: 'basketball',
    label: 'Basketball',
    aliases: ['basketball', 'bball'],
    allowsTies: false,
    scoring: points,
    standingsDefault: pctStandings,
    launchEnabled: true,
  },
  baseball: {
    id: 'baseball',
    label: 'Baseball',
    aliases: ['baseball'],
    allowsTies: false,
    scoring: runs,
    standingsDefault: pctStandings,
    launchEnabled: true,
  },
  softball: {
    id: 'softball',
    label: 'Softball',
    aliases: ['softball'],
    allowsTies: false,
    scoring: runs,
    standingsDefault: pctStandings,
    launchEnabled: true,
  },
  flag_football: {
    id: 'flag_football',
    label: 'Flag Football',
    aliases: ['flag_football', 'flagfootball', 'flag'],
    allowsTies: true,
    scoring: points,
    // NFL convention: win pct with ties as half-wins.
    standingsDefault: pctStandings,
    launchEnabled: true,
  },
};

/** Build a flat alias -> canonical id lookup once. */
const ALIAS_LOOKUP: Record<string, CanonicalSport> = (() => {
  const map: Record<string, CanonicalSport> = {};
  for (const meta of Object.values(SPORT_REGISTRY)) {
    map[meta.id] = meta.id;
    for (const alias of meta.aliases) {
      map[alias] = meta.id;
    }
  }
  return map;
})();

/**
 * Normalize a free-text sport value (as stored on `leagues.sport`) to a
 * canonical sport id. Returns `null` for unknown / unsupported values so
 * callers can apply their own fallback.
 *
 * Replaces the copy-pasted `sport.toLowerCase().replace(/[\s-]/g, '_')`
 * snippets scattered across the codebase.
 */
export function normalizeSport(input: string | null | undefined): CanonicalSport | null {
  if (!input) return null;
  const key = input.toLowerCase().trim().replace(/[\s-]/g, '_');
  return ALIAS_LOOKUP[key] ?? null;
}

/** Type guard for canonical sport ids. */
export function isCanonicalSport(value: string | null | undefined): value is CanonicalSport {
  return !!value && value in SPORT_REGISTRY;
}

/** Get full metadata for a sport (accepts free-text or canonical id). */
export function getSportMeta(sport: string | null | undefined): SportMeta | undefined {
  const id = normalizeSport(sport);
  return id ? SPORT_REGISTRY[id] : undefined;
}

/**
 * Get the display label for a sport. Falls back to the raw input when the
 * sport is unknown (preserves prior `formatSportDisplayName` behavior).
 */
export function getSportLabel(sport: string | null | undefined): string {
  return getSportMeta(sport)?.label ?? (sport ?? '');
}

/** Get scoring terminology for a sport, defaulting to points when unknown. */
export function getScoringTerminology(sport: string | null | undefined): ScoringTerminology {
  return getSportMeta(sport)?.scoring ?? points;
}

/** Whether a sport allows ties. Unknown sports default to `false`. */
export function sportAllowsTies(sport: string | null | undefined): boolean {
  return getSportMeta(sport)?.allowsTies ?? false;
}

/** List every canonical sport id. */
export function listSports(): CanonicalSport[] {
  return Object.keys(SPORT_REGISTRY) as CanonicalSport[];
}

/**
 * The sports currently offered to users when creating a league. Drive every
 * sport picker from this so launching a new sport is a one-line registry edit.
 */
export function listEnabledSports(): SportMeta[] {
  return Object.values(SPORT_REGISTRY).filter((meta) => meta.launchEnabled);
}

/**
 * Whether a sport belongs to the baseball family (baseball or softball).
 * These two share stat catalogues, line-score format and scoring terminology,
 * so they are frequently handled together. Centralized here so adding another
 * baseball-like sport is a one-line change.
 */
export function isBaseballFamily(sport: string | null | undefined): boolean {
  const id = normalizeSport(sport);
  return id === 'baseball' || id === 'softball';
}

/** Whether a sport is soccer (normalization-robust). */
export function isSoccer(sport: string | null | undefined): boolean {
  return normalizeSport(sport) === 'soccer';
}

/** Whether a sport is basketball (normalization-robust). */
export function isBasketball(sport: string | null | undefined): boolean {
  return normalizeSport(sport) === 'basketball';
}
